import { useQuery } from "@tanstack/react-query";

interface RelayTokenPriceResponse {
  price: number;
}

async function fetchRelayTokenPrice(tokenAddress: string, chainId: number): Promise<number> {
  if (!tokenAddress) throw new Error("Token address is required");
  if (!chainId) throw new Error("Chain ID is required");

  const params = new URLSearchParams({
    address: tokenAddress,
    chainId: chainId.toString(),
  });

  const url = `https://api.relay.link/currencies/token/price?${params.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch token price: ${response.statusText}`);
  }

  const data: RelayTokenPriceResponse = await response.json();
  return data.price;
}

interface UseRelayTokenPriceOptions {
  tokenAddress?: string;
  chainId?: number;
  enabled?: boolean;
}

export function useRelayTokenPrice({ tokenAddress, chainId, enabled = true }: UseRelayTokenPriceOptions) {
  return useQuery({
    queryKey: ["relay-token-price", tokenAddress, chainId],
    queryFn: () => {
      if (!tokenAddress || !chainId) {
        throw new Error("Missing required parameters");
      }
      return fetchRelayTokenPrice(tokenAddress, chainId);
    },
    enabled: enabled && Boolean(tokenAddress) && Boolean(chainId),
    staleTime: 1 * 60 * 1000, // 1 minute (prices change frequently)
    retry: 1, // Only retry once for price lookups
  });
}
